import { cloneDeep } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { hasColumnChanged } from '../data-grid-functions';
import type { DataGridColumnSettings } from '../data-grid-settings.interface';
import type { DataGridFilterProps } from './data-grid-filter.interface';
import { ContinuousScrollerComparisonType } from '../../continuous-scroller/continuous-scroller-types';
import TextSearchField from './text-search-field.component';

export interface DataGridSearchFilterProps extends DataGridFilterProps<string> {
  id: string;
  initialValue?: string;
}

export default function DataGridSearchFilter({
  column,
  id,
  initialValue,
  filterSignal,
  forceResetColumnMap,
}: DataGridSearchFilterProps) {
  const [currentValue, setCurrentValue] = useState<string>(initialValue ?? (column.searchValue as string) ?? '');
  const previousColumn = useRef<DataGridColumnSettings | undefined>(undefined);

  useEffect(() => {
    if (hasColumnChanged(forceResetColumnMap, column, previousColumn.current)) {
      setCurrentValue((column.searchValue as string) ?? '');
    }

    previousColumn.current = column;
  }, [column, forceResetColumnMap]);

  useEffect(() => {
    if (currentValue.length > 0) {
      const clonedFilters = cloneDeep(filterSignal.value);
      clonedFilters.push({
        scrollerId: column.id,
        field: column.field,
        customDataProperties: column.customDataProperties,
        comparison: column.searchComparison ?? ContinuousScrollerComparisonType.Contains,
        value: currentValue,
      });
      filterSignal.value = clonedFilters;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Mount-only effect to initialize filter from initial column values
  }, []);

  const onInputChanged = useCallback((searchText?: string) => {
    if (searchText && searchText.length > 0) {
      const filterIndex = filterSignal.value.findIndex((filter) => filter.scrollerId === column.id);
      const clonedFilters = cloneDeep(filterSignal.value);
      if (filterIndex > -1) {
        clonedFilters.splice(filterIndex, 1, {
          scrollerId: column.id,
          field: column.field,
          customDataProperties: column.customDataProperties,
          comparison: column.searchComparison ?? ContinuousScrollerComparisonType.Contains,
          value: searchText,
        });
        filterSignal.value = clonedFilters;
      } else {
        clonedFilters.push({
          scrollerId: column.id,
          field: column.field,
          customDataProperties: column.customDataProperties,
          comparison: column.searchComparison ?? ContinuousScrollerComparisonType.Contains,
          value: searchText,
        });
        filterSignal.value = clonedFilters;
      }

      setCurrentValue(searchText);
    } else {
      if (filterSignal.value.findIndex((filter) => filter.scrollerId === column.id) > -1) {
        filterSignal.value = filterSignal.value.filter((filter) => filter.scrollerId !== column.id);
      }
      setCurrentValue('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- column and filterSignal are stable objects
  }, []);

  return <TextSearchField id={id} currentValue={currentValue} onInputChanged={onInputChanged} />;
}
